import { useCallback, useMemo, useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";
import type {
  ClipCompilationPageProps,
  CompilationFilters,
  CompilationListItem,
  PendingCompilationRequest,
  RequestCompilationFormProps,
} from "@visioni/a3s-media/app";
import {
  DEFAULT_PAGE_SIZE,
  useCompilation,
  useCompilationClipCount,
  useCompilationsList,
  useRequestCompilation,
  useRequestCompilationForm,
} from "@visioni/a3s-media/app";
import { AssetCameraKey, SiteId } from "@visioni/a3s-schema";
import { toZonedTime } from "date-fns-tz";
import { toISODateString } from "@visioni/a3s-codecs";
import type { SiteListItem } from "@visioni/a3s-provisioning/client-messages";
import { useSites } from "@src/features/cameras/hooks";
import { useCameras } from "@src/services/api/hooks";
import type { CameraApiResponse } from "@src/features/cameras";
import { getCameraKeyString, formatCameraLabel } from "@src/utils/key-parser";
import { downloadVideo, handleDownloadError } from "@src/features/clips";
import { useIsSupportUser } from "@src/features/tenants";
import { showErrorToast } from "@src/utils/toast";
import { useRecordCompilationViewed } from "./useRecordCompilationViewed";

const DEFAULT_FILTERS: CompilationFilters = {
  dateRange: { start: null, end: null },
  status: null,
  siteId: null,
  cameraId: null,
};

const getSiteId = (s: SiteListItem): string => SiteId.toString(s.siteId);
const getSiteLabel = (s: SiteListItem): string => s.siteName;
const getCameraId = (c: CameraApiResponse): string => getCameraKeyString(c);
const getCameraLabel = (c: CameraApiResponse): string => c.nickname || formatCameraLabel(getCameraKeyString(c));

export interface UseClipCompilationPagePropsResult {
  readonly pageProps: ClipCompilationPageProps<SiteListItem, CameraApiResponse>;
  /** Request dialog state — the route renders `RequestCompilationForm` inside
   * a `DialogModal` controlled by these, since `ClipCompilationPage` itself
   * only exposes `onRequestCompilation` as a bare trigger. */
  readonly isRequestDialogOpen: boolean;
  readonly closeRequestDialog: () => void;
  /**
   * Everything `RequestCompilationForm` needs.
   *
   * Assembled here rather than in the routes so the web and native screens
   * cannot disagree about when the form may be submitted — the clip-count gate
   * has to be applied alongside the form's own validity, and that is one
   * decision, not two. `maxDate` belongs here for the same reason: the latest
   * selectable day is "today" at the camera, which the routes cannot work out
   * without the camera's timezone.
   */
  readonly requestFormProps: RequestCompilationFormProps<CameraApiResponse>;
  /** Watch view state — the route swaps the list for `CompilationWatchView`
   * in place when set, rather than navigating (see that component's doc for why). */
  readonly watchingItem: CompilationListItem | null;
  readonly closeWatchView: () => void;
}

/**
 * Wires `ClipCompilationPage` to real data — `useCompilationsList`/
 * `useRequestCompilation` (`@visioni/a3s-media/app`), and `sites`/`cameras`
 * from this app's own existing sources (`useSites`, `useCameras`) rather than
 * a new a3s-media query. Mirrors `useActivityPageProps`'s shape.
 */
export function useClipCompilationPageProps(): UseClipCompilationPagePropsResult {
  const [filters, setFiltersState] = useState<CompilationFilters>(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [isRequestDialogOpen, setRequestDialogOpen] = useState(false);

  /**
   * Any filter change returns to the first page. Without this a reader sitting
   * on page 5 who narrows the filters lands on a page that no longer exists
   * and sees an empty table rather than the rows they asked for.
   */
  const setFilters = useCallback((next: CompilationFilters) => {
    setFiltersState(next);
    setPage(1);
  }, []);
  const router = useRouter();
  // Only the id round-trips through the URL (not the signed URLs themselves —
  // see `CompilationWatchView`'s doc for why) so refreshing keeps the watch
  // view open; the actual item (with its signed URLs) is looked up from the
  // already-loaded list below once `items` is computed.
  // `site` and `camera` accompany `watching` on links built outside the app
  // (the "compilation ready" email). In-app navigation sets only `watching`,
  // because it can only be triggered from a row that is already on screen.
  const {
    watching: watchingRequestId,
    site: watchingSiteId,
    camera: watchingCameraKey,
  } = useLocalSearchParams<{ watching?: string; site?: string; camera?: string }>();
  const isSupportUser = useIsSupportUser();

  const { sites } = useSites();
  const { data: cameras } = useCameras();

  const cameraNames = useMemo(
    () => new Map((cameras ?? []).map((c) => [getCameraKeyString(c), getCameraLabel(c)])),
    [cameras],
  );

  /**
   * Cameras whose compilations the reader can actually be shown.
   *
   * `useCameras` is permission-filtered server-side, but on `camera:view` —
   * whereas the listing is scoped to `clips:view`. The two are granted
   * together by both standard permission sets, so today they select the same
   * cameras; only a hand-edited per-camera grant separates them, and without
   * this filter such a reader could pick a camera and get a silently empty
   * table. `allowedActions` is the per-camera action list the cameras API has
   * already resolved, so narrowing costs no extra request.
   *
   * A camera without `allowedActions` is kept rather than hidden: the field is
   * optional, so an API version that predates it degrades to the old
   * behaviour instead of emptying the picker.
   */
  const visibleCameras = useMemo(
    () => (cameras ?? []).filter((c) => c.allowedActions?.includes("clips:view") ?? true),
    [cameras],
  );

  /**
   * Only the sites the reader has a visible camera at — the listing is scoped
   * to those cameras, so any other site filters to nothing. `useSites` lists
   * every site in the tenant, which is why it needs narrowing.
   */
  const visibleSites = useMemo(() => {
    const sitesWithCameras = new Set(visibleCameras.map((c) => c.site.id));
    return (sites ?? []).filter((s) => sitesWithCameras.has(getSiteId(s)));
  }, [sites, visibleCameras]);

  const list = useCompilationsList({
    siteId: filters.siteId ?? undefined,
    cameraKey: filters.cameraId ?? undefined,
    dateRange: filters.dateRange.start || filters.dateRange.end ? filters.dateRange : undefined,
    status: filters.status ?? undefined,
    cameraNames,
    page,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Every filter resolves in the database, so the rows are used as they
  // arrive. Nothing is discarded here, which is what keeps `totalCount` — and
  // therefore the page count — honest.
  const items = list.items;

  const listedWatchingItem = useMemo(
    () => (watchingRequestId ? (items.find((item) => item.id === watchingRequestId) ?? null) : null),
    [items, watchingRequestId],
  );

  // The list only holds the page on screen, so a deep link to an older
  // compilation finds nothing there — the common case for an emailed link
  // read hours after the job finished. Fetch it directly instead, but only
  // when the list has already come up short, so ordinary in-app navigation
  // costs no extra request.
  const fetched = useCompilation({
    siteId: watchingSiteId,
    assetCameraKey: watchingCameraKey,
    requestId: watchingRequestId,
    cameraNames,
    enabled: !!watchingRequestId && !listedWatchingItem && !list.isLoading,
  });

  const watchingItem = listedWatchingItem ?? fetched.item;

  // The mutation's own error is not read: a failed request is surfaced as a
  // toast from the `catch` below, and the dialog stays open to retry.
  const { requestCompilation, isPending } = useRequestCompilation();

  const onRequestSubmit = useCallback(
    (request: PendingCompilationRequest) => {
      const camera = cameras?.find((c) => getCameraKeyString(c) === request.cameraId);
      if (!camera) return;

      // The instants come from the form, which resolved them in the camera's
      // timezone. Re-deriving them from the picked dates here would read the
      // same wall clock in the *viewer's* zone and request a window other than
      // the one the dialog showed and counted.
      void requestCompilation({
        siteId: SiteId.fromString(camera.site.id),
        assetCameraKey: AssetCameraKey.fromString(getCameraKeyString(camera)),
        body: {
          startTime: toISODateString(request.startTime),
          endTime: toISODateString(request.endTime),
        },
      })
        .then(() => setRequestDialogOpen(false))
        .catch((error: unknown) => {
          // Dialog stays open so the user can retry without re-entering the form.
          showErrorToast({ error, description: "Could not request the compilation. Please try again." });
        });
    },
    [cameras, requestCompilation],
  );

  /**
   * The camera's own timezone is what the entered dates and times mean. The
   * site is the authoritative source (`_provisioning_sites.timezone` — the
   * same column the vehicle tracker reads, see
   * `docs/vehicle-tracker-timezones.md`); a site with none recorded returns
   * undefined and the form falls back to the device's zone.
   */
  const timezoneFor = useCallback(
    (cameraId: string): string | undefined => {
      const camera = cameras?.find((c) => getCameraKeyString(c) === cameraId);
      const site = camera && sites?.find((s) => getSiteId(s) === camera.site.id);
      return site?.timezone ?? undefined;
    },
    [cameras, sites],
  );

  const form = useRequestCompilationForm({ onSubmit: onRequestSubmit, loading: isPending, timezoneFor });

  // The count is camera-scoped and the form only tracks the camera key, so the
  // site comes from the camera list — the same lookup `onRequestSubmit` makes
  // to address the POST.
  const pendingCamera = cameras?.find((c) => getCameraKeyString(c) === form.pendingRequest?.cameraId);

  const clipCount = useCompilationClipCount({
    siteId: pendingCamera ? SiteId.fromString(pendingCamera.site.id) : undefined,
    assetCameraKey: pendingCamera ? AssetCameraKey.fromString(getCameraKeyString(pendingCamera)) : undefined,
    startTime: form.pendingRequest?.startTime ?? null,
    endTime: form.pendingRequest?.endTime ?? null,
    enabled: isRequestDialogOpen,
  });

  const { onSubmit: submitForm } = form;
  // Guards the submit itself, not just the button: `canSubmit` below disables
  // the button on the same condition, but the form's own submit path must not
  // be reachable past the gate either.
  const submitIfCounted = useCallback(() => {
    if (clipCount.blocksRequest) return;
    submitForm();
  }, [clipCount.blocksRequest, submitForm]);

  const onWatch = useCallback(
    (item: CompilationListItem) => {
      if (!item.videoUrl) return;
      // Site and camera travel with the id so the link addresses the
      // compilation on its own. Without them the watch view can only find it
      // by searching the page currently loaded, so paging away or refreshing
      // dropped the viewer back to the list.
      router.setParams({ watching: item.id, site: item.siteId, camera: item.cameraKey });
    },
    [router],
  );

  const recordViewed = useRecordCompilationViewed();

  const onDownload = useCallback(
    (item: CompilationListItem) => {
      if (!item.downloadUrl) return;
      recordViewed(item, "download");
      // `attachment` because the download URL is signed with a filename, so it
      // carries `Content-Disposition: attachment` and genuinely downloads rather
      // than opening in a tab — which is what the toast then tells the user.
      void downloadVideo({ videoUrl: item.downloadUrl, timestamp: new Date(), attachment: true }).catch(
        handleDownloadError,
      );
    },
    [recordViewed],
  );

  return {
    pageProps: {
      items,
      isLoading: list.isLoading,
      error: list.error != null,
      totalCount: list.totalCount,
      page,
      pageSize: DEFAULT_PAGE_SIZE,
      onPageChange: setPage,
      isFetching: list.isFetching,
      onRequestCompilation: isSupportUser ? () => setRequestDialogOpen(true) : undefined,
      onWatch,
      onDownload,
      filters,
      onFiltersChange: setFilters,
      sites: visibleSites,
      cameras: visibleCameras,
      getSiteLabel,
      getSiteId,
      getCameraLabel,
      getCameraId,
    },
    isRequestDialogOpen,
    closeRequestDialog: () => setRequestDialogOpen(false),
    requestFormProps: {
      cameras: cameras ?? [],
      getCameraId,
      getCameraLabel,
      camera: form.camera,
      timezone: form.timezone,
      // "Today" at the camera, which is a day ahead of the viewer's own for a
      // camera far enough east — the days the picker offers have to be the
      // camera's, since that is the calendar the times are read against.
      maxDate: toZonedTime(new Date(), form.timezone),
      startDate: form.startDate,
      startTime: form.startTime,
      endDate: form.endDate,
      endTime: form.endTime,
      // Two gates applied as one: the form's own validity, and whether the
      // clip count says there is anything in the range to compile.
      canSubmit: form.canSubmit && !clipCount.blocksRequest,
      onSubmit: submitIfCounted,
      loading: isPending,
      clipCount: clipCount.estimate,
    },
    watchingItem,
    closeWatchView: () => router.setParams({ watching: "" }),
  };
}
